<script setup lang="ts">
  // In-tab order view (draft mode 'view'). Resolves a store-backed reactive
  // order exactly like ModalPosViewOrder.vue: prefer the ordersStore instance
  // (live socket updates), keep it even if the store later removes it, and
  // fall back to a fetched snapshot for orders outside the pending set
  // (e.g. archived ones).
  import { computed, onBeforeMount, ref, watch } from 'vue';
  import OrdersService from '@/api/services/orders.service';
  import { IOrder } from '@/interfaces/IOrder';
  import { useOrdersStore } from '@/stores/orders.store';
  import { usePosDraftsStore } from '@/stores/pos-drafts.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosV2ViewActions from './PosV2ViewActions.vue';
  import PosV2ViewWorkspaceBase from './PosV2ViewWorkspaceBase.vue';

  const props = defineProps<{
    orderId: number;
  }>();

  const ordersStore = useOrdersStore();
  const posDrafts = usePosDraftsStore();
  const restaurantStore = useRestaurantStore();

  const storeOrder = computed<IOrder | null>(() => {
    const orders = ordersStore.orders;
    if (!orders) return null;
    return orders.find((o) => o.id === props.orderId) ?? null;
  });

  const providedOrder = ref<IOrder | null>(null);
  const loading = ref(false);

  watch(
    storeOrder,
    (o) => {
      if (o) providedOrder.value = o;
    },
    { immediate: true }
  );

  // Restored view stubs default to Seat until hydration: write the real type
  // back to the owning draft so the tab label matches the order.
  watch(
    providedOrder,
    (order) => {
      if (!order) return;
      const draft = posDrafts.drafts.find(
        (d) => d.orderId === props.orderId && d.mode === 'view'
      );
      if (draft && draft.type !== order.type) {
        posDrafts.syncViewDraftType(draft.id, order.type);
      }
    },
    { immediate: true }
  );

  onBeforeMount(async () => {
    if (providedOrder.value) return;

    const restaurantId = restaurantStore.restaurant?.id;
    if (!restaurantId) return;

    loading.value = true;
    try {
      const fetched = await OrdersService.v2GetOrderById(
        restaurantId,
        props.orderId
      );
      // The store instance may have arrived (socket) while fetching; it wins.
      if (!providedOrder.value) providedOrder.value = fetched;
    } catch (e) {
      console.error('Error fetching order for view tab', e);
    } finally {
      loading.value = false;
    }
  });
</script>

<template>
  <PosV2ViewWorkspaceBase
    :order="providedOrder"
    :loading="loading"
    mode="interactive"
  >
    <template #footer>
      <PosV2ViewActions />
    </template>
  </PosV2ViewWorkspaceBase>
</template>
